import React, { useState } from "react";
import {
  UserIcon,
  UsersIcon,
  GlobeAltIcon,
  ChevronRightIcon,
  ViewfinderCircleIcon,
} from "@heroicons/react/24/outline";

// 07 Mar 2026
import EtTrinityPurposeNativeOnBoarding from "./EtTrinityPurposeNativeOnBoarding"

// 09 Mar 2026
import EtAscensionTrailIcon1SVGComp from "@/assets/img/icons/svgComp/EtAscensionTrailIcon1SVGComp";
import EtAscensionBaseCampIcon1SVGComp from "@/assets/img/icons/svgComp/EtAscensionBaseCampIcon1SVGComp";

const EtTrinityPurposeNativeBoard = ({ username, userId, userOrgId }) => {
  // Author - Atul Pandey
  // First Created - 03 Mar 2026
  // Purpose to act as the purpose native home page for Trinity Landing
  // Updated - 03 Mar 2026 - Step 1: Added Self | Collective | Universe tabs + Board shell blocks (placeholders)
  // Updated - 05 Mar 2026 - JSX refinements for Onboarding section
  // Updated - 06 Mar 2026 - JSX refinements for Overall and Onboarding section
  // Updated - 07 Mar 2026 - Added the onboarding section as a separate comp for simplification
  // Updated - 09 Mar 2026 - Continued refinements of JSX
  // Updated - 27 Mar 2026 - Commented the "Your Ascent" till Base Camp is fully done

  // 1️⃣ Tabs
  const [activeBoardTab, setActiveBoardTab] = useState("self"); // self | collective | universe
  const [activeTopTab, setActiveTopTab] = useState("begin"); // 05 Mar 2026 - begin | board 

  // 2️⃣ Placeholder data (we will wire these to real computations next)
  const ascentLevels = [
    { level: 1, label: "Awareness" },
    { level: 2, label: "Discipline" },
    { level: 3, label: "Alignment" },
    { level: 4, label: "Durability" },
    { level: 5, label: "Impact" },
    { level: 6, label: "Influence" },
  ];

  const mock = {
    self: {
      currentLevel: 2,
      title: "You are building Discipline.",
      subtitle: "Your consistency is visible. Alignment is forming, but not yet reliable.",
      nextUnlock: "Alignment",
      progressPct: 60,
      weakestLeverTitle: "Mission linkage is your growth lever.",
      weakestLeverDesc:
        "Most actions are active, but not tied to your mission. Discipline without alignment will stall progress.",
      recommendedMoveTitle: "This week:",
      recommendedMoveDesc: "Link 2 existing actions to your mission and close 1 before its due date.",
      momentum: [
        { label: "Reflections (21d)", value: "7" },
        { label: "Active Actions", value: "3" },
        { label: "Creations", value: "1" },
        { label: "Mission Linked %", value: "33%" },
        { label: "Overdue %", value: "25%" },
      ],
      unlockRequirements: [
        { label: "Mission linked actions", value: "2 / 3" },
        { label: "Mission linked closures", value: "1 / 2" },
        { label: "Overdue rate", value: "Needs improvement" },
      ],
      stability: { label: "Moderate", hint: "Overdue actions creeping up" },
    },
    collective: {
      currentLevel: 2,
      title: "Your organization is building Discipline.",
      subtitle: "Consistency is visible. Alignment is uneven across the cohort.",
      weakestLeverTitle: "Mission linkage consistency is your growth lever.",
      weakestLeverDesc:
        "Discipline is present, but mission alignment is not yet uniform across leaders.",
      recommendedMoveTitle: "This month:",
      recommendedMoveDesc: "Drive mission linking in weekly reviews and reduce overdue actions.",
      distribution: [
        { level: 1, pct: 10 },
        { level: 2, pct: 62 },
        { level: 3, pct: 28 },
        { level: 4, pct: 10 },
        { level: 5, pct: 0 },
        { level: 6, pct: 0 },
      ],
      stability: { label: "Steady", hint: "Alignment variance remains" },
    },
    universe: {
      currentLevel: 2,
      title: "Your organization is building Discipline in the Trinity Universe.",
      subtitle: "You are ahead of many peers on discipline, but alignment trails the median.",
      peerContext: "Peer Cluster: Tech Services | 1000–3000 employees",
      signals: [
        { label: "Universe median level", value: "Level 3 – Alignment" },
        { label: "Your percentile (Discipline)", value: "Top 35%" },
        { label: "Unlock velocity", value: "Above median" },
        { label: "Stability", value: "Strong" },
      ],
      recommendedMoveTitle: "Next lift:",
      recommendedMoveDesc: "Increase mission linked closures to move the cohort into Alignment.",
      stability: { label: "Strong", hint: "Low regression frequency" },
    },
  };

  const activeData =
    activeBoardTab === "self"
      ? mock.self
      : activeBoardTab === "collective"
      ? mock.collective
      : mock.universe;

  // 3️⃣ Small helper: tab button style
  const tabBtnClass = (key) =>
    `flex items-center px-3 py-2 rounded cursor-pointer shadow-sm border ${
      activeBoardTab === key
        ? "bg-black text-orange-600 border-black"
        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
    }`;

  return (
    <div className="p-2 font-poppins">

      {/* 0️⃣ Top Level Tabs */}
      <div className="bg-white rounded shadow-sm border p-3">
        <div className="grid grid-cols-5 justify-start items-center space-x-2">
          <div className="col-span-2 flex items-center space-x-2">
            <button
            onClick={() => setActiveTopTab("begin")}
            className={`flex items-center px-3 py-2 rounded-full cursor-pointer shadow-sm border ${
              activeTopTab === "begin"
                ? "bg-orange-600 text-white font-bold"
                : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
            }`}
          >
            <EtAscensionBaseCampIcon1SVGComp className={`h-6 w-6 ${
              activeTopTab === "begin" ? "": "text-orange-600"}`}/>
            <span className="text-xs mx-1 px-2">Base Camp</span>
          </button>

          {/* 27 Mar 2026 - Commented till Base Camp is fully done */}
          {/* <button
            onClick={() => setActiveTopTab("board")}
            className={`flex items-center px-3 py-2 rounded-full cursor-pointer shadow-sm border ${
              activeTopTab === "board"
                ? "bg-orange-600 text-white font-bold"
                : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
            }`}
          >
            <EtAscensionTrailIcon1SVGComp className={`h-6 w-6 ${
              activeTopTab === "board" ? "": "text-orange-600"}`}/>
            <span className="text-xs mx-1 px-2">Your Ascent</span>
          </button> */}
         
          </div>
          <div className="col-span-3">
            <p className="text-2xl mx-4 px-4">{ activeTopTab === "begin" ? "Gear up for your Purpose Native Journey!" : "See how much you have scaled!"}</p>
          </div>
          
          
        </div>
         
      </div>

      {/* 1️⃣ Begin Here */}
      {activeTopTab === "begin" && (
        <EtTrinityPurposeNativeOnBoarding setActiveTopTab={setActiveTopTab}/>
      )}

      {/* 2️⃣ Purpose Native Board Header + Tabs */}
      {activeTopTab === "board" && (
        <div>
            <div className="mt-2 bg-white rounded shadow-sm border p-3">
              <div>
                <p className="text-sm font-bold text-gray-800">Purpose Native Board</p>
                <p className="text-xs text-gray-600 mt-1">
                  Track your journey across Self, Collective, and Universe.
                </p>
              </div>

              {/* Board Tabs */}
              <div className="mt-3 flex items-center space-x-2">
                <button className={tabBtnClass("self")} onClick={() => setActiveBoardTab("self")}>
                  <UserIcon className="h-4 w-4" />
                  <span className="text-xs font-bold ml-2">Self</span>
                </button>

                <button
                  className={tabBtnClass("collective")}
                  onClick={() => setActiveBoardTab("collective")}
                >
                  <UsersIcon className="h-4 w-4" />
                  <span className="text-xs font-bold ml-2">Collective</span>
                </button>

                <button
                  className={tabBtnClass("universe")}
                  onClick={() => setActiveBoardTab("universe")}
                >
                  <GlobeAltIcon className="h-4 w-4" />
                  <span className="text-xs font-bold ml-2">Universe</span>
                </button>
              </div>
            </div>
        

            {/* 2️⃣ Ascent Spine + Narrative */}
            <div className="mt-2 grid grid-cols-12 gap-2">

              {/* Ascent Spine */}
              <div className="col-span-12 lg:col-span-4 bg-white rounded shadow-sm border p-3">
                <p className="text-xs font-bold text-gray-700 mb-2">Ascent</p>

                <div className="space-y-2">
                  {ascentLevels.map((item) => {
                    const isCurrent = item.level === activeData.currentLevel;
                    const isAbove = item.level > activeData.currentLevel;

                    return (
                      <div
                        key={`ascent-${activeBoardTab}-${item.level}`}
                        className={`flex items-center justify-between rounded px-2 py-2 border ${
                          isCurrent
                            ? "bg-black text-orange-600 border-black"
                            : isAbove
                            ? "bg-gray-50 text-gray-500 border-gray-200"
                            : "bg-white text-gray-700 border-gray-200"
                        }`}
                      >
                        <div className="flex items-center">
                          <div
                            className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold border ${
                              isCurrent
                                ? "border-orange-600"
                                : "border-gray-300 text-gray-600"
                            }`}
                          >
                            {item.level}
                          </div>
                          <p className="ml-2 text-xs font-bold">{item.label}</p>
                        </div>

                        {isCurrent && (
                          <div className="flex items-center">
                            <span className="text-[10px] font-bold mr-2">You are here</span>
                            <ChevronRightIcon className="h-4 w-4" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Stability */}
                <div className="mt-3 bg-gray-50 border border-gray-200 rounded p-2">
                  <p className="text-xs font-bold text-gray-700">
                    Stability: <span className="text-orange-600">{activeData?.stability?.label}</span>
                  </p>
                  <p className="text-xs text-gray-600 mt-1">{activeData?.stability?.hint}</p>
                </div>
              </div>

              {/* Narrative + Next Unlock */}
              <div className="col-span-12 lg:col-span-8 space-y-2">

                {/* Current Narrative */}
                <div className="bg-white rounded shadow-sm border p-3">
                  <p className="text-sm font-bold text-gray-800">{activeData.title}</p>
                  <p className="text-xs text-gray-600 mt-1 leading-5">{activeData.subtitle}</p>
                </div>

                {/* Next Unlock (Self only) */}
                {activeBoardTab === "self" && (
                  <div className="bg-white rounded shadow-sm border p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold text-gray-700">Next Unlock</p>
                      <p className="text-xs font-bold text-orange-600">{mock.self.nextUnlock}</p>
                    </div>

                    {/* Progress bar (simple) */}
                    <div className="mt-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-600">Progress</p>
                        <p className="text-xs font-bold text-gray-700">{mock.self.progressPct}%</p>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded mt-1">
                        <div
                          className="h-2 bg-orange-600 rounded"
                          style={{ width: `${mock.self.progressPct}%` }}
                        />
                      </div>
                    </div>

                    {/* Requirements */}
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                      {mock.self.unlockRequirements.map((req, idx) => (
                        <div
                          key={`unlock-req-${idx}`}
                          className="bg-gray-50 border border-gray-200 rounded p-2"
                        >
                          <p className="text-[10px] font-bold text-gray-700">{req.label}</p>
                          <p className="text-xs text-gray-700 mt-1">{req.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Collective Distribution (Collective only) */}
                {activeBoardTab === "collective" && (
                  <div className="bg-white rounded shadow-sm border p-3">
                    <p className="text-xs font-bold text-gray-700 mb-2">Level Distribution</p>

                    <div className="space-y-2">
                      {mock.collective.distribution.map((d) => {
                        const label = ascentLevels.find((l) => l.level === d.level)?.label || `Level ${d.level}`;
                        return (
                          <div key={`dist-${d.level}`} className="flex items-center">
                            <p className="w-28 text-xs text-gray-700">
                              {d.level}. {label}
                            </p>
                            <div className="flex-1 h-2 bg-gray-200 rounded mx-2">
                              <div
                                className="h-2 bg-orange-600 rounded"
                                style={{ width: `${d.pct}%` }}
                              />
                            </div>
                            <p className="text-xs font-bold text-gray-700 w-10 text-right">{d.pct}%</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Universe Signals (Universe only) */}
                {activeBoardTab === "universe" && (
                  <div className="bg-white rounded shadow-sm border p-3">
                    <p className="text-xs font-bold text-gray-700">Universe Context</p>
                    <p className="text-xs text-gray-600 mt-1">{mock.universe.peerContext}</p>

                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                      {mock.universe.signals.map((s, idx) => (
                        <div key={`uni-sig-${idx}`} className="bg-gray-50 border border-gray-200 rounded p-2">
                          <p className="text-[10px] font-bold text-gray-700">{s.label}</p>
                          <p className="text-xs font-bold text-gray-800 mt-1">{s.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Weakest Lever */}
                <div className="bg-white rounded shadow-sm border p-3">
                  <p className="text-xs font-bold text-gray-700">Weakest Lever</p>
                  <p className="text-sm font-bold text-gray-800 mt-1">{activeData.weakestLeverTitle}</p>
                  <p className="text-xs text-gray-600 mt-1 leading-5">{activeData.weakestLeverDesc}</p>
                </div>

                {/* Recommended Move */}
                <div className="bg-white rounded shadow-sm border p-3">
                  <p className="text-xs font-bold text-gray-700">Recommended Move</p>
                  <p className="text-sm font-bold text-gray-800 mt-1">{activeData.recommendedMoveTitle}</p>
                  <p className="text-xs text-gray-600 mt-1 leading-5">{activeData.recommendedMoveDesc}</p>

                  {/* CTA placeholder */}
                  <button className="mt-3 bg-black text-orange-600 px-3 py-2 rounded text-xs font-bold shadow-sm hover:bg-gray-900">
                    Take this step
                  </button>
                </div>

                {/* Momentum Snapshot (Self only for now) */}
                {activeBoardTab === "self" && (
                  <div className="bg-white rounded shadow-sm border p-3">
                    <p className="text-xs font-bold text-gray-700 mb-2">Momentum Snapshot</p>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      {mock.self.momentum.map((m, idx) => (
                        <div key={`mom-${idx}`} className="bg-gray-50 border border-gray-200 rounded p-2">
                          <p className="text-[10px] font-bold text-gray-700">{m.label}</p>
                          <p className="text-sm font-bold text-gray-800 mt-1">{m.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default EtTrinityPurposeNativeBoard;